import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useMonetization } from '../../contexts/MonetizationContext';
import { useTranslation } from '../../contexts/LanguageContext';
import { hasAdConsent } from '../CookieConsent';
import { trackEvent } from '../../utils/analytics';
import { recordAdImpression } from '../../services/api';
import { isTestAdEnvironment } from '../../utils/adUtils';
import { gatherAdConsent } from '../../utils/adConsent';
import './AdBanner.css';

// Web AdSense banners stay off until AdSense approves the site; native AdMob is unaffected.
export const WEB_BANNERS_ENABLED = false;

/**
 * Set the --ad-bottom-height CSS variable on :root.
 * Used only for web (AdSense) bottom banner spacing.
 */
function setAdBottomHeight(px) {
    document.documentElement.style.setProperty('--ad-bottom-height', `${px}px`);
}

/**
 * Detect if running on an emulator/simulator (not a real device).
 */
function isEmulator() {
    const ua = navigator.userAgent || '';
    if (/sdk_gphone|Emulator|Android SDK|google_sdk/i.test(ua)) return true;
    if (/Simulator/i.test(ua)) return true;
    return false;
}

/**
 * Get the AdMob ad unit ID for the current platform.
 * Uses Google's demo test IDs on emulators/simulators for reliable test ads.
 */
function getAdMobUnitId() {
    const platform = window.Capacitor?.getPlatform?.() || 'web';
    const useTestIds = isEmulator() || process.env.NODE_ENV === 'development';

    if (platform === 'ios') {
        return useTestIds
            ? 'ca-app-pub-3940256099942544/2934735716'
            : (process.env.REACT_APP_ADMOB_BANNER_ID_IOS || 'ca-app-pub-3940256099942544/2934735716');
    } else if (platform === 'android') {
        return useTestIds
            ? 'ca-app-pub-3940256099942544/6300978111'
            : (process.env.REACT_APP_ADMOB_BANNER_ID_ANDROID || 'ca-app-pub-3940256099942544/6300978111');
    }
    return 'test-ad-unit';
}

/**
 * AdBanner component for displaying web AdSense banner ads.
 * On native mobile, this component returns null — AdMob is managed by AdBannerLayout.
 */
function AdBanner({ position = 'bottom', size = 'banner' }) {
    const { adsRemoved, isNativeMobile, adRefreshTrigger, loading } = useMonetization();
    const { t } = useTranslation();
    const [adLoaded, setAdLoaded] = useState(false);
    const [adError, setAdError] = useState(false);
    const [adKey, setAdKey] = useState(0);
    const adContainerRef = useRef(null);
    const adInitialized = useRef(false);

    const AD_SIZES = {
        'banner': { width: 320, height: 50 },
        'large-banner': { width: 320, height: 100 },
        'medium-rectangle': { width: 300, height: 250 },
        'leaderboard': { width: 728, height: 90 },
    };
    const adSize = AD_SIZES[size] || AD_SIZES['banner'];

    const AD_SLOTS = {
        'bottom': process.env.REACT_APP_ADSENSE_SLOT_BOTTOM || process.env.REACT_APP_ADSENSE_SLOT || '',
        'left': process.env.REACT_APP_ADSENSE_SLOT_LEFT || process.env.REACT_APP_ADSENSE_SLOT || '',
        'right': process.env.REACT_APP_ADSENSE_SLOT_RIGHT || process.env.REACT_APP_ADSENSE_SLOT || '',
    };
    const adSlot = AD_SLOTS[position] || AD_SLOTS['bottom'];

    const initAdSense = useCallback(() => {
        if (!process.env.REACT_APP_ADSENSE_CLIENT) return;
        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            setAdLoaded(true);
            if (position === 'bottom') setAdBottomHeight(50);

            const insEl = adContainerRef.current?.querySelector('ins.adsbygoogle');
            if (insEl) {
                const observer = new MutationObserver(() => {
                    if (insEl.getAttribute('data-ad-status') === 'filled') {
                        observer.disconnect();
                        const rect = insEl.getBoundingClientRect();
                        if (rect.height > 1 && rect.width > 1 && !isTestAdEnvironment()) {
                            trackEvent('Ad Impression', { ad_type: 'banner', position });
                            recordAdImpression({ adType: 'banner', position });
                        }
                    } else if (insEl.getAttribute('data-ad-status') === 'unfilled') {
                        observer.disconnect();
                        setAdLoaded(false);
                        setAdError(true);
                        if (position === 'bottom') setAdBottomHeight(0);
                    }
                });
                observer.observe(insEl, { attributes: true, attributeFilter: ['data-ad-status'] });
                setTimeout(() => observer.disconnect(), 10000);
            }
        } catch (error) {
            console.error('AdSense error:', error);
            setAdError(true);
            if (position === 'bottom') setAdBottomHeight(0);
        }
    }, [position]);

    useEffect(() => {
        if (loading || adsRemoved || adInitialized.current || isNativeMobile()) return;
        if (!hasAdConsent()) {
            const handleConsentChange = (e) => {
                if (e.detail?.advertising && !adInitialized.current) {
                    adInitialized.current = true;
                    initAdSense();
                }
            };
            window.addEventListener('cookieConsentChanged', handleConsentChange);
            return () => window.removeEventListener('cookieConsentChanged', handleConsentChange);
        }
        adInitialized.current = true;
        initAdSense();
    }, [loading, adsRemoved, isNativeMobile, position, initAdSense]);

    useEffect(() => {
        if (adRefreshTrigger === 0 || adsRemoved || isNativeMobile()) return;
        setAdKey(prev => prev + 1);
        setAdLoaded(false);
        adInitialized.current = false;
        setTimeout(() => initAdSense(), 100);
    }, [adRefreshTrigger, adsRemoved, isNativeMobile, initAdSense]);

    useEffect(() => {
        if (adsRemoved && position === 'bottom') setAdBottomHeight(0);
    }, [adsRemoved, position]);

    if (adsRemoved || isNativeMobile()) return null;

    const containerClass = `ad-banner ad-banner-${position} ${adLoaded ? 'ad-loaded' : ''} ${adError ? 'ad-error' : ''}`;

    return (
        <div className={containerClass} ref={adContainerRef}>
            {process.env.REACT_APP_ADSENSE_CLIENT && (
                <ins
                    key={`ad-${position}-${adKey}`}
                    className="adsbygoogle"
                    style={{
                        display: 'inline-block',
                        width: position === 'bottom' ? '100%' : adSize.width,
                        height: adSize.height,
                        maxHeight: adSize.height,
                    }}
                    data-ad-client={process.env.REACT_APP_ADSENSE_CLIENT}
                    data-ad-slot={adSlot}
                    data-ad-format={position === 'bottom' ? 'horizontal' : 'auto'}
                    data-full-width-responsive={position === 'bottom' ? 'false' : 'true'}
                />
            )}
            {adError && position !== 'bottom' && (
                <div className="ad-placeholder">
                    <span>{t('ads.advertisement')}</span>
                </div>
            )}
        </div>
    );
}

/**
 * Wrapper component that manages the full ad layout:
 * - Side banners (web only, >900px)
 * - Bottom banner (web: AdSense via AdBanner, native: AdMob managed here)
 * - Footer in normal document flow
 * - Native ad spacer rendered as a real DOM element via React state
 */
/**
 * Set / clear the static --native-ad-height CSS variable on :root.
 * .ad-layout-content uses it for padding-bottom reservation.
 * This is called ONLY on AdMob lifecycle events (loaded, size changed,
 * failed, removed) — NEVER on keyboard events.
 *
 * Accepts the RAW banner height (as reported by AdMob). Adds +10px
 * internally for layout spacing (--native-ad-height). Also exposes the
 * raw height via window.__wlNativeAdRawHeight for the keyboard formula
 * in native.js, which needs the exact webview frame reduction (no padding).
 */
function setNativeAdHeightVar(rawHeight) {
    if (!rawHeight) {
        document.documentElement.style.setProperty('--native-ad-height', '0px');
        window.__wlNativeAdRawHeight = 0;
    } else {
        document.documentElement.style.setProperty('--native-ad-height', `${rawHeight + 10}px`);
        window.__wlNativeAdRawHeight = rawHeight;
    }
}

function waitForTutorialEnd() {
    const inTutorial = () => document.body.classList.contains('wl-onboarding');
    if (!inTutorial()) return Promise.resolve();
    return new Promise(resolve => {
        const obs = new MutationObserver(() => { if (!inTutorial()) { obs.disconnect(); resolve(); } });
        obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    });
}

export function AdBannerLayout({ children, footer }) {
    const { adsRemoved, isNativeMobile, loading } = useMonetization();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const adMobInitialized = useRef(false);
    const lastBannerWidth = useRef(window.innerWidth);

    // Initialize --ad-bottom-height to 0 on mount (for web)
    useEffect(() => {
        setAdBottomHeight(0);
    }, []);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ========== Native AdMob management ==========
    //
    // NOTE: ad height is written to the --native-ad-height CSS variable on
    // :root (via setNativeAdHeightVar). It is NEVER changed on keyboard
    // events — .ad-layout-content's padding-bottom reserves the space
    // statically so the keyboard-open flow has zero reactive layout changes.

    useEffect(() => {
        if (!isNativeMobile() || adsRemoved || loading || adMobInitialized.current) return;

        const AdMob = window.Capacitor?.Plugins?.AdMob;
        if (!AdMob) return;

        adMobInitialized.current = true;
        const listeners = [];

        const fallbackHeightFor = () =>
            Math.min(window.innerWidth, window.innerHeight) >= 600 ? 90 : 50;

        const initNativeBanner = async () => {
            try {
                listeners.push(await AdMob.addListener('bannerAdSizeChanged', (size) => {
                    if (size.height > 0) {
                        setNativeAdHeightVar(size.height);
                    }
                }));
                listeners.push(await AdMob.addListener('bannerAdLoaded', () => {
                    const current = getComputedStyle(document.documentElement)
                        .getPropertyValue('--native-ad-height').trim();
                    if (!current || current === '0px' || current === '') {
                        setNativeAdHeightVar(fallbackHeightFor());
                    }
                    if (!isTestAdEnvironment()) {
                        trackEvent('Ad Impression', { ad_type: 'banner', position: 'bottom' });
                        recordAdImpression({ adType: 'banner', position: 'bottom' });
                    }
                }));
                listeners.push(await AdMob.addListener('bannerAdFailedToLoad', () => {
                    setNativeAdHeightVar(0);
                }));

                // New players see the tutorial first; consent dialogs and the banner wait until it ends.
                await waitForTutorialEnd();
                // Consent (UMP form, then iOS ATT) must come before any ad request.
                const consent = await gatherAdConsent();
                if (consent?.canRequestAds === false) {
                    setNativeAdHeightVar(0);
                    return;
                }

                await new Promise(resolve => setTimeout(resolve, 500));

                await AdMob.showBanner({
                    adId: getAdMobUnitId(),
                    adSize: 'ADAPTIVE_BANNER',
                    position: 'BOTTOM_CENTER',
                    margin: 0,
                    isTesting: process.env.NODE_ENV === 'development',
                });

                // showBanner resolves before ad loads — set fallback once
                const current = getComputedStyle(document.documentElement)
                    .getPropertyValue('--native-ad-height').trim();
                if (!current || current === '0px' || current === '') {
                    setNativeAdHeightVar(fallbackHeightFor());
                }
                lastBannerWidth.current = window.innerWidth;
            } catch (error) {
                console.error('AdMob banner error:', error);
                setNativeAdHeightVar(0);
            }
        };

        initNativeBanner();

        return () => {
            listeners.forEach(l => l?.remove?.());
        };
    }, [isNativeMobile, adsRemoved, loading]);

    // Remove native AdMob banner when adsRemoved becomes true
    useEffect(() => {
        if (!adsRemoved || !isNativeMobile()) return;

        setNativeAdHeightVar(0);

        const removeBanner = () => {
            try {
                if (window.Capacitor?.Plugins?.AdMob) {
                    window.Capacitor.Plugins.AdMob.hideBanner();
                    window.Capacitor.Plugins.AdMob.removeBanner();
                }
            } catch (error) {
                console.error('Error hiding AdMob banner:', error);
            }
        };

        removeBanner();
        const retries = [2000, 5000, 10000, 20000, 30000];
        const timers = retries.map(delay => setTimeout(removeBanner, delay));
        return () => timers.forEach(clearTimeout);
    }, [adsRemoved, isNativeMobile]);

    // Reload native AdMob banner on orientation change (width change > 50px)
    // Adaptive banners are sized for the screen width at load time, so after
    // rotation we must remove and re-show to get a correctly sized banner.
    useEffect(() => {
        if (!isNativeMobile() || adsRemoved) return;

        let debounceTimer = null;

        const handleOrientationResize = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
                const newWidth = window.innerWidth;
                if (Math.abs(newWidth - lastBannerWidth.current) < 50) return;

                const AdMob = window.Capacitor?.Plugins?.AdMob;
                if (!AdMob) return;

                lastBannerWidth.current = newWidth;

                try {
                    await AdMob.removeBanner();
                    setNativeAdHeightVar(0);

                    await new Promise(resolve => setTimeout(resolve, 300));

                    await AdMob.showBanner({
                        adId: getAdMobUnitId(),
                        adSize: 'ADAPTIVE_BANNER',
                        position: 'BOTTOM_CENTER',
                        margin: 0,
                        isTesting: process.env.NODE_ENV === 'development',
                    });

                    const fallbackHeight = Math.min(window.innerWidth, window.innerHeight) >= 600 ? 90 : 50;
                    const current = getComputedStyle(document.documentElement)
                        .getPropertyValue('--native-ad-height').trim();
                    if (!current || current === '0px' || current === '') {
                        setNativeAdHeightVar(fallbackHeight);
                    }
                } catch (error) {
                    console.error('AdMob banner reload on rotation error:', error);
                }
            }, 300);
        };

        window.addEventListener('resize', handleOrientationResize);
        return () => {
            clearTimeout(debounceTimer);
            window.removeEventListener('resize', handleOrientationResize);
        };
    }, [isNativeMobile, adsRemoved]);

    const showWebBanners = WEB_BANNERS_ENABLED && !adsRemoved && !isNativeMobile();
    const showSideBanners = showWebBanners && windowWidth > 900;

    return (
        <div className={`ad-layout ${adsRemoved ? 'ads-removed' : ''}`}>
            {showSideBanners && <AdBanner position="left" size="medium-rectangle" />}

            <div className="ad-layout-content">
                {children}
                {footer}
            </div>

            {showWebBanners && <AdBanner position="bottom" size="banner" />}
            {showSideBanners && <AdBanner position="right" size="medium-rectangle" />}
        </div>
    );
}

export default AdBanner;
